import { HStack, IconButton } from '@chakra-ui/react';

import { MaterialIcon } from '../../ui/MaterialIcon';
import type { SessionRatingValue } from './submitSessionRating';

interface SessionRatingThumbsProps {
  /** Names the thing being rated in each button's accessible name. */
  readonly subject: string;
  readonly isDisabled: boolean;
  readonly onRate: (value: SessionRatingValue) => void;
  /** 44 px targets, for the Live toast, which is mostly tapped on a phone. */
  readonly touch?: boolean;
}

/**
 * The two one-tap buttons of the session-end rating. Same icons and colors as
 * the per-message thumb (`MessageFeedback` in `BirdMessage.tsx`), and its size
 * in the chat row, so the two read as one control family; the accessible names
 * say a session is being rated, not a single reply.
 */
export function SessionRatingThumbs({
  subject,
  isDisabled,
  onRate,
  touch = false,
}: SessionRatingThumbsProps) {
  // A literal: the theme's space scale has no `11`, and an unknown step
  // passes through as 11 px.
  const target = touch ? '44px' : 6;
  const iconSize = touch ? 5 : 3.5;
  return (
    <HStack spacing={0.5}>
      <IconButton
        aria-label={`Good ${subject}`}
        size="xs"
        variant="ghost"
        icon={<MaterialIcon name="thumb_up" boxSize={iconSize} color="text.muted" />}
        isDisabled={isDisabled}
        onClick={() => onRate('up')}
        minW={target}
        h={target}
        _hover={{ bg: 'bg.hover' }}
      />
      <IconButton
        aria-label={`Bad ${subject}`}
        size="xs"
        variant="ghost"
        icon={<MaterialIcon name="thumb_down" boxSize={iconSize} color="text.muted" />}
        isDisabled={isDisabled}
        onClick={() => onRate('down')}
        minW={target}
        h={target}
        _hover={{ bg: 'bg.hover' }}
      />
    </HStack>
  );
}
